/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * The persistent softphone.
 *
 * A round, draggable floating button (collapsed) that expands into the call
 * panel. Position is persisted so agents can park it clear of scrollbars and
 * other controls (e.g. the Leads board horizontal scroller).
 *
 * Mounted beside <Routes> so a call survives navigation. Deliberately NOT
 * wrapped in scaledVh/scaledVw: appScale.js applies `body { zoom: 0.8 }`, and a
 * fixed element already resolves against the scaled containing block.
 *
 * When the Call Console is mounted it registers as the inline host and this
 * collapses to the FAB -- one engine, one visible set of controls.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Card,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Snackbar,
  TextField,
} from "@mui/material";
import {
  Backspace,
  Call,
  CallEnd,
  Dialpad,
  ExpandLess,
  ExpandMore,
  Mic,
  MicOff,
  PhoneInTalk,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MonoText from "components/MonoText";
import { useTelephony } from "telephony/TelephonyContext";
import {
  AGENT_PRESENCE,
  CALL_STATE,
  CALL_STATE_META,
  PRESENCE_META,
  formatDuration,
} from "telephony/callStates";
import { Dialpad as DialpadKeys, DTMF_KEYS, PresenceDot, RoundButton } from "telephony/components";
import { isDialable, sanitizeDialInput } from "telephony/dialNumber";
import { callerIdLabel, distinctFriendlyName } from "telephony/callerIdLabel";
import { getCssZoom } from "assets/theme/base/appScale";
import colors from "styles/tokens/colors";

const cc = colors.callCenter;

const DARK = cc.dark;
const SURFACE = cc.surface;
const MUTED_TEXT = cc.muted;

const FAB_SIZE = 56;
const PANEL_WIDTH = 300;
const PANEL_EST_HEIGHT = 320;
const MARGIN = 24;
const DRAG_THRESHOLD_PX = 6;
// v2: right/bottom offsets. The first left/top key stored unzoomed coords that
// landed off-screen under body { zoom: 0.8 }.
const STORAGE_KEY = "pearl.softphone.dockPosition.v2";

function defaultPosition() {
  return { right: MARGIN, bottom: MARGIN };
}

/**
 * Layout size in CSS pixels (pre-zoom). Fixed `right`/`bottom` are applied in
 * this space, then body zoom shrinks them to visual px — same as the old dock.
 */
function layoutViewport() {
  if (typeof window === "undefined") {
    return { width: 1280, height: 800 };
  }
  const zoom = getCssZoom() || 1;
  return {
    width: window.innerWidth / zoom,
    height: window.innerHeight / zoom,
  };
}

function readStoredPosition() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.right === "number" &&
      typeof parsed?.bottom === "number" &&
      Number.isFinite(parsed.right) &&
      Number.isFinite(parsed.bottom)
    ) {
      return { right: parsed.right, bottom: parsed.bottom };
    }
  } catch {
    /* ignore corrupt storage */
  }
  return null;
}

function clampPosition(pos, size = FAB_SIZE) {
  const { width, height } = layoutViewport();
  const maxRight = Math.max(MARGIN, width - MARGIN - size);
  const maxBottom = Math.max(MARGIN, height - MARGIN - size);
  return {
    right: Math.min(maxRight, Math.max(MARGIN, pos.right)),
    bottom: Math.min(maxBottom, Math.max(MARGIN, pos.bottom)),
  };
}

export default function SoftphoneDock() {
  const navigate = useNavigate();
  const {
    enabled,
    settingsChecked,
    isLeader,
    registered,
    deviceError,
    clearError,
    micError,
    dismissMicError,
    presence,
    changePresence,
    callState,
    activeCall,
    durationSec,
    muted,
    acceptCall,
    rejectCall,
    hangUp,
    toggleMute,
    sendDigits,
    placeCall,
    skipWrapUp,
    inlineHostActive,
    callerIds,
    fromNumber,
    setFromNumberId,
  } = useTelephony();

  const [collapsed, setCollapsed] = useState(true);
  const [presenceAnchor, setPresenceAnchor] = useState(null);
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [dialerOpen, setDialerOpen] = useState(false);
  const [dialNumber, setDialNumber] = useState("");
  const [dialBusy, setDialBusy] = useState(false);
  const [dialError, setDialError] = useState("");
  const [position, setPosition] = useState(() =>
    clampPosition(readStoredPosition() || defaultPosition()),
  );
  const [dragging, setDragging] = useState(false);

  const dragRef = useRef(null);
  // Pointer-driven drag: track origin so a tap still toggles expand/collapse.
  const pointerRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    originLeft: 0,
    originTop: 0,
    moved: false,
  });

  const persistPosition = useCallback((pos) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
    } catch {
      /* quota / private mode */
    }
  }, []);

  useEffect(() => {
    const onResize = () => setPosition((p) => clampPosition(p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Nothing to show until we know telephony exists for this tenant.
  if (!settingsChecked || !enabled) return null;

  const meta = CALL_STATE_META[callState] || CALL_STATE_META[CALL_STATE.IDLE];
  const presenceMeta = PRESENCE_META[presence] || PRESENCE_META.offline;
  const isRinging = callState === CALL_STATE.RINGING;
  const isLive = callState === CALL_STATE.CONNECTED || callState === CALL_STATE.ON_HOLD;
  const isWrapUp = callState === CALL_STATE.WRAP_UP;
  const hasCall = callState !== CALL_STATE.IDLE;

  // The Call Console owns the controls while it is mounted.
  const asPill = inlineHostActive && !isRinging;
  const expanded = (!collapsed || isRinging) && !asPill;

  const counterparty =
    activeCall?.direction === "outbound" ? activeCall?.to_address : activeCall?.from_address;
  // Our side of the call: the caller ID on an outbound call, the number the
  // customer dialled on an inbound one.
  const ourLine =
    activeCall?.direction === "outbound" ? activeCall?.from_address : activeCall?.to_address;

  const isAvailable = presence === "available";
  const blockedReason = !isLeader
    ? "The softphone is active in another browser tab."
    : !registered
    ? "Your softphone is still connecting."
    : !isAvailable
    ? `You are ${(presenceMeta.label || "offline").toLowerCase()}. Go Available to place calls.`
    : "";
  const canDial = !blockedReason && !dialBusy && isDialable(dialNumber);
  const dialHint = dialError || blockedReason;

  const dial = async () => {
    if (!canDial) return;
    setDialBusy(true);
    setDialError("");
    try {
      await placeCall({ to: dialNumber });
      setDialNumber("");
      setDialerOpen(false);
    } catch (err) {
      setDialError(err?.message || "The call could not be placed.");
    } finally {
      setDialBusy(false);
    }
  };

  const endDrag = (event) => {
    const drag = pointerRef.current;
    if (!drag.active) return;
    if (event?.pointerId != null && drag.pointerId !== event.pointerId) return;

    drag.active = false;
    setDragging(false);
    try {
      if (drag.pointerId != null) {
        dragRef.current?.releasePointerCapture?.(drag.pointerId);
      }
    } catch {
      /* already released */
    }

    if (!drag.moved && !isRinging) {
      setCollapsed((c) => !c);
    } else if (drag.moved) {
      setPosition((p) => {
        const next = clampPosition(p);
        persistPosition(next);
        return next;
      });
    }
  };

  const onPointerDown = (event) => {
    // Only primary button / touch; ignore right-click.
    if (event.button != null && event.button !== 0) return;
    event.preventDefault();
    pointerRef.current = {
      active: true,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originRight: position.right,
      originBottom: position.bottom,
      moved: false,
    };
    setDragging(true);
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      /* older browsers */
    }
  };

  const onPointerMove = (event) => {
    const drag = pointerRef.current;
    if (!drag.active || drag.pointerId !== event.pointerId) return;

    const visualDx = event.clientX - drag.startX;
    const visualDy = event.clientY - drag.startY;
    if (
      !drag.moved &&
      visualDx * visualDx + visualDy * visualDy >= DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX
    ) {
      drag.moved = true;
    }
    if (!drag.moved) return;

    // Pointer coords are visual px; fixed right/bottom are CSS px (pre-zoom).
    const zoom = getCssZoom() || 1;
    const dx = visualDx / zoom;
    const dy = visualDy / zoom;
    // Dragging right/down decreases the distance from those edges.
    setPosition(
      clampPosition({
        right: drag.originRight - dx,
        bottom: drag.originBottom - dy,
      }),
    );
  };

  // Expand toward the center of the screen from wherever the FAB sits.
  const { width: layoutW, height: layoutH } = layoutViewport();
  const fabCenterX = layoutW - position.right - FAB_SIZE / 2;
  const fabCenterY = layoutH - position.bottom - FAB_SIZE / 2;
  const openLeft = fabCenterX >= layoutW / 2;
  const openUp = fabCenterY >= layoutH / 2;

  const fabLabel = hasCall
    ? `${meta.label}${isLive ? ` ${formatDuration(durationSec)}` : ""}`
    : "Softphone — drag to move, click to open";

  return (
    <>
      <MDBox
        ref={dragRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        role="complementary"
        aria-label="Softphone"
        sx={{
          position: "fixed",
          right: position.right,
          bottom: position.bottom,
          width: FAB_SIZE,
          height: FAB_SIZE,
          // Above the sidenav drawer so the FAB is never buried under chrome.
          zIndex: (theme) => theme.zIndex.drawer + 2,
          // Panel extends outside the FAB box; keep it visible.
          overflow: "visible",
          touchAction: "none",
          userSelect: "none",
          cursor: dragging ? "grabbing" : "grab",
        }}
      >
        {!expanded ? (
          <IconButton
            aria-label={fabLabel}
            aria-expanded={false}
            sx={{
              width: FAB_SIZE,
              height: FAB_SIZE,
              bgcolor: isRinging ? "#1565C8" : DARK,
              color: "#fff",
              borderRadius: "50%",
              border: "2px solid #fff",
              boxShadow: "0 8px 24px rgba(14,35,72,0.22)",
              pointerEvents: "none", // parent handles pointer for drag vs click
              animation: isRinging ? "pearlRing 1.6s ease-in-out infinite" : "none",
              "@keyframes pearlRing": {
                "0%,100%": { opacity: 1 },
                "50%": { opacity: 0.72 },
              },
              "&:hover": { bgcolor: isRinging ? "#1565C8" : DARK },
            }}
          >
            <PhoneInTalk sx={{ fontSize: 26 }} />
            <MDBox
              sx={{
                position: "absolute",
                right: 6,
                bottom: 6,
                width: 12,
                height: 12,
                borderRadius: "50%",
                bgcolor: presenceMeta.color || MUTED_TEXT,
                border: "2px solid #fff",
              }}
            />
          </IconButton>
        ) : (
          <Card
            sx={{
              position: "absolute",
              width: asPill ? 220 : PANEL_WIDTH,
              ...(openUp ? { bottom: 0 } : { top: 0 }),
              ...(openLeft ? { right: 0 } : { left: 0 }),
              borderRadius: "12px",
              border: "1px solid #E8EDF5",
              boxShadow: "0 8px 24px rgba(14,35,72,0.14)",
              overflow: "hidden",
              cursor: "default",
            }}
            // Clicks inside the panel must not start a drag from the wrapper.
            onPointerDown={(e) => e.stopPropagation()}
          >
            <MDBox
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              sx={{
                bgcolor: isRinging ? "#1565C8" : DARK,
                px: 1.5,
                height: 40,
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: dragging ? "grabbing" : isRinging ? "default" : "grab",
                touchAction: "none",
                animation: isRinging ? "pearlRing 1.6s ease-in-out infinite" : "none",
                "@keyframes pearlRing": {
                  "0%,100%": { opacity: 1 },
                  "50%": { opacity: 0.72 },
                },
              }}
            >
              <PhoneInTalk sx={{ color: "#fff", fontSize: 18 }} />
              <MDTypography variant="button" color="white" sx={{ flexGrow: 1 }}>
                {hasCall ? meta.label : "Softphone"}
              </MDTypography>
              {isLive && (
                <MonoText variant="button" sx={{ color: "#fff" }}>
                  {formatDuration(durationSec)}
                </MonoText>
              )}
              {!isRinging &&
                (expanded ? (
                  <ExpandMore sx={{ color: "#fff", fontSize: 18 }} />
                ) : (
                  <ExpandLess sx={{ color: "#fff", fontSize: 18 }} />
                ))}
            </MDBox>

            <Collapse in={expanded}>
              <MDBox p={2}>
                {!isLeader && (
                  <Alert severity="info" sx={{ mb: 1.5, py: 0.25 }}>
                    Softphone active in another tab.
                  </Alert>
                )}
                {isLeader && !registered && !deviceError && (
                  <Alert severity="info" sx={{ mb: 1.5, py: 0.25 }}>
                    Connecting…
                  </Alert>
                )}
                {deviceError && (
                  <Alert severity="warning" sx={{ mb: 1.5, py: 0.25 }} onClose={clearError}>
                    {deviceError.message}
                  </Alert>
                )}

                {hasCall ? (
                  <>
                    <MDBox mb={1.5}>
                      <MDTypography variant="button" fontWeight="bold" color="dark" display="block">
                        {activeCall?.caller_name ||
                          activeCall?.contact_name ||
                          activeCall?.account_name ||
                          "Unknown caller"}
                      </MDTypography>
                      {counterparty && <MonoText variant="caption">{counterparty}</MonoText>}
                      {ourLine && (
                        <MDTypography
                          variant="caption"
                          display="block"
                          sx={{ color: MUTED_TEXT, mt: 0.25 }}
                        >
                          {activeCall?.direction === "outbound" ? "Calling from " : "Line "}
                          <MonoText variant="caption" component="span">
                            {ourLine}
                          </MonoText>
                        </MDTypography>
                      )}
                    </MDBox>

                    {isRinging ? (
                      <MDBox display="flex" justifyContent="space-around">
                        <RoundButton icon={<Call />} label="Accept" onClick={acceptCall} active />
                        <RoundButton
                          icon={<CallEnd />}
                          label="Decline"
                          onClick={rejectCall}
                          danger
                        />
                      </MDBox>
                    ) : isWrapUp ? (
                      <MDBox display="flex" gap={1}>
                        <MDButton
                          fullWidth
                          variant="outlined"
                          color="info"
                          size="small"
                          // In-app navigation, not a page load: a reload threw
                          // away the wrap-up state, so the console opened on the
                          // dialler with nothing to save.
                          onClick={() =>
                            navigate(
                              activeCall?.id
                                ? `/call-console?call=${activeCall.id}`
                                : "/call-console",
                            )
                          }
                        >
                          Add outcome
                        </MDButton>
                        <MDButton
                          fullWidth
                          variant="gradient"
                          color="info"
                          size="small"
                          onClick={skipWrapUp}
                        >
                          Done
                        </MDButton>
                      </MDBox>
                    ) : (
                      <>
                        <MDBox display="flex" justifyContent="space-around">
                          <RoundButton
                            icon={muted ? <MicOff /> : <Mic />}
                            label={muted ? "Unmute" : "Mute"}
                            onClick={toggleMute}
                            active={muted}
                            disabled={!isLive}
                          />
                          <RoundButton
                            icon={<Dialpad />}
                            label="Keypad"
                            onClick={() => setKeypadOpen((k) => !k)}
                            active={keypadOpen}
                            disabled={!isLive}
                          />
                          <RoundButton
                            icon={<CallEnd />}
                            label="Hang up"
                            onClick={hangUp}
                            danger
                            disabled={!isLive}
                          />
                        </MDBox>

                        <Collapse in={keypadOpen}>
                          <MDBox
                            mt={2}
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "repeat(3, 1fr)",
                              gap: 0.75,
                            }}
                          >
                            {DTMF_KEYS.map((key) => (
                              <IconButton
                                key={key}
                                onClick={() => sendDigits(key)}
                                sx={{
                                  borderRadius: "8px",
                                  bgcolor: SURFACE,
                                  color: DARK,
                                  fontWeight: 600,
                                  "&:hover": { bgcolor: "#E8EDF5" },
                                }}
                              >
                                <MonoText variant="button">{key}</MonoText>
                              </IconButton>
                            ))}
                          </MDBox>
                        </Collapse>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ cursor: "pointer" }}
                      onClick={(e) => setPresenceAnchor(e.currentTarget)}
                    >
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <PresenceDot color={presenceMeta.color} />
                        <MDTypography variant="button" color="dark">
                          {presenceMeta.label}
                        </MDTypography>
                      </MDBox>
                      <ExpandLess sx={{ fontSize: 18, color: MUTED_TEXT }} />
                    </MDBox>

                    <Divider sx={{ my: 1.5 }} />

                    <MDBox display="flex" alignItems="center" justifyContent="space-between">
                      <MDTypography variant="caption" color="text" fontWeight="medium">
                        Dial a number
                      </MDTypography>
                      <IconButton
                        size="small"
                        onClick={() => setDialerOpen((d) => !d)}
                        aria-label={dialerOpen ? "Hide dialler" : "Show dialler"}
                        aria-expanded={dialerOpen}
                        sx={{ color: dialerOpen ? "#1565C8" : MUTED_TEXT }}
                      >
                        <Dialpad sx={{ fontSize: 18 }} />
                      </IconButton>
                    </MDBox>

                    <Collapse in={dialerOpen}>
                      <MDBox mt={1}>
                        {callerIds.length > 1 ? (
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Calling from"
                            value={fromNumber?.id ?? ""}
                            onChange={(e) => setFromNumberId(Number(e.target.value))}
                            sx={{ mb: 1 }}
                          >
                            {callerIds.map((n) => (
                              <MenuItem key={n.id} value={n.id}>
                                {callerIdLabel(n)}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : fromNumber ? (
                          <MDTypography
                            variant="caption"
                            display="block"
                            sx={{ color: MUTED_TEXT, mb: 1 }}
                          >
                            Calling from{" "}
                            <MonoText variant="caption" component="span">
                              {fromNumber.e164}
                            </MonoText>
                            {distinctFriendlyName(fromNumber) ? ` (${distinctFriendlyName(fromNumber)})` : ""}
                          </MDTypography>
                        ) : null}
                        <TextField
                          fullWidth
                          size="small"
                          value={dialNumber}
                          onChange={(e) => setDialNumber(sanitizeDialInput(e.target.value))}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && canDial) dial();
                          }}
                          placeholder="+61…"
                          inputProps={{ inputMode: "tel", "aria-label": "Number to dial" }}
                          InputProps={{
                            endAdornment: dialNumber ? (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  aria-label="Delete last digit"
                                  onClick={() => setDialNumber((n) => n.slice(0, -1))}
                                >
                                  <Backspace sx={{ fontSize: 16 }} />
                                </IconButton>
                              </InputAdornment>
                            ) : null,
                          }}
                        />

                        <MDBox mt={1}>
                          <DialpadKeys
                            size="small"
                            onKey={(key) => setDialNumber((n) => `${n}${key}`)}
                            disabled={dialBusy}
                          />
                        </MDBox>

                        {dialHint ? (
                          <MDTypography
                            variant="caption"
                            color="text"
                            display="block"
                            sx={{ mt: 1 }}
                          >
                            {dialHint}
                          </MDTypography>
                        ) : null}

                        <MDButton
                          fullWidth
                          variant="gradient"
                          color="success"
                          size="small"
                          sx={{ mt: 1 }}
                          disabled={!canDial}
                          onClick={dial}
                          startIcon={<Call />}
                        >
                          {dialBusy ? "Calling…" : "Call"}
                        </MDButton>
                      </MDBox>
                    </Collapse>
                  </>
                )}
              </MDBox>
            </Collapse>
          </Card>
        )}
      </MDBox>

      <Menu
        anchorEl={presenceAnchor}
        open={Boolean(presenceAnchor)}
        onClose={() => setPresenceAnchor(null)}
      >
        {AGENT_PRESENCE.map((p) => (
          <MenuItem
            key={p.value}
            selected={p.value === presence}
            onClick={async () => {
              setPresenceAnchor(null);
              await changePresence(p.value);
            }}
          >
            <MDBox display="flex" alignItems="center" gap={1}>
              <PresenceDot color={p.color} size={8} />
              <MDTypography variant="button">{p.label}</MDTypography>
            </MDBox>
          </MenuItem>
        ))}
      </Menu>

      <Snackbar
        open={Boolean(micError)}
        onClose={dismissMicError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={dismissMicError} sx={{ maxWidth: 420 }}>
          <MDTypography variant="button" fontWeight="bold" display="block">
            {micError?.message}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            Click the lock icon in the address bar, allow the microphone, then set yourself Available
            again.
          </MDTypography>
        </Alert>
      </Snackbar>
    </>
  );
}
